import sliderCss from 'blaze-slider/dist/blaze.css?url'

import {createFileRoute} from '@tanstack/react-router'
import imageUrlBuilder from '@sanity/image-url'
import {HiLockClosed} from 'react-icons/hi2'
import {useTranslations} from 'use-intl'
import {Suspense, useMemo} from 'react'
import {
    UpsellingProducts,
    UpsellingProductsSkeletonLoader,
} from '~/components/basket/UpsellingProducts'
import {useBasketItemQuantityMutation, useBasketQuery} from '~/hooks/useBasket'
import BasketSkeletonLoader from '~/components/basket/BasketSkeletonLoader'
import {BasketErrorCard} from '~/components/basket/BasketErrorCard'
import {EmptyBasketCard} from '~/components/basket/EmptyBasketCard'
import {getBasketPageContent} from '~/client/fn/basketPageContent'
import {BasketItemList} from '~/components/basket/BasketItemList'
import BasketSummary from '~/components/basket/BasketSummary'
import {Button} from '~/components/common/Button'
import {shopQueryOptions} from '~/client/fn/shop'
import Card from '~/components/common/Card'
import seo from '~/utils/seo'

export const Route = createFileRoute('/$lang/_layout/basket')({
    component: RouteComponent,
    loader: async ({context}) => {
        const [pageContent, shopData] = await Promise.all([
            getBasketPageContent({data: {locale: context.locale}}),
            context.queryClient.ensureQueryData(shopQueryOptions()),
        ])

        return {
            pageContent,
            shopData,
        }
    },
    head: ({loaderData, params}) => {
        const lang = params.lang
        const canonicalUrl = `https://gravity-shirts.com/${lang}/basket`

        if (!loaderData?.pageContent?.seo) {
            return {
                meta: [{name: 'robots', content: 'noindex, follow'}],
                links: [
                    {
                        rel: 'preload',
                        href: sliderCss,
                        as: 'style',
                    },
                    {
                        rel: 'stylesheet',
                        href: sliderCss,
                    },
                ],
            }
        }

        const seoData = loaderData.pageContent.seo
        const builder = imageUrlBuilder({...loaderData.shopData.cms})
        const metaTags = seo({
            title: seoData.title ?? undefined,
            description: seoData.description ?? undefined,
            ogUrl: canonicalUrl,
            locale: lang,
            image: seoData.image
                ? {
                      src: builder.image(seoData.image).width(1200).height(630).url(),
                      width: 1200,
                      height: 630,
                  }
                : undefined,
        })

        return {
            meta: [...metaTags, {name: 'robots', content: 'noindex, follow'}],
            links: [
                {
                    rel: 'preload',
                    href: sliderCss,
                    as: 'style',
                },
                {
                    rel: 'stylesheet',
                    href: sliderCss,
                },
            ],
        }
    },
})

function RouteComponent() {
    const {pageContent} = Route.useLoaderData()
    const t = useTranslations('basket')
    const {data: basket, isLoading, isFetching, isError, error} = useBasketQuery()
    const {mutate: updateBasketItemQuantity, isPending} = useBasketItemQuantityMutation()

    const isBasketEmpty = !isLoading && !basket?.basketItems.length
    const isUpdatingBasket = isFetching || isPending

    const checkoutLink = useMemo(() => {
        const baseHref = basket?.links.find((link) => link.type === 'shopCheckout')?.href

        if (!baseHref) {
            return basket?.links[0]?.href ?? undefined
        }

        const basketUrl = new URL(baseHref)
        basketUrl.searchParams.set('continueShoppingLink', window.location.href)

        return basketUrl.toString()
    }, [basket?.links])

    return (
        <div
            className="px-4 py-6 mx-auto max-w-screen-xl lg:py-12 sm:px-6"
            aria-busy={isLoading}
            aria-live="polite"
        >
            <h1 className="text-2xl font-medium mb-4">{t('title')}</h1>
            {isLoading && <BasketSkeletonLoader />}
            {!isLoading && (
                <div>
                    {isError && <BasketErrorCard error={error} />}
                    {isBasketEmpty && <EmptyBasketCard />}

                    {!isBasketEmpty && basket && (
                        <>
                            <Card className="grid grid-cols-1 md:grid-cols-2">
                                <BasketItemList
                                    basketItems={basket.basketItems}
                                    onQuantityChange={updateBasketItemQuantity}
                                />
                                <div className="mt-2 border-t md:mt-0 md:border-t-0 md:border-l border-gray-600">
                                    <div className="md:sticky md:top-16 md:pb-4">
                                        <BasketSummary
                                            className="md:p-6 lg:p-8"
                                            basket={basket}
                                            isRecalculating={isUpdatingBasket}
                                        />
                                        <div className="mx-4 mt-6 md:m-6 lg:m-8 hidden md:block space-y-3">
                                            <Button
                                                className="w-full"
                                                as="a"
                                                href={checkoutLink}
                                            >
                                                {t('checkout')}
                                            </Button>
                                            <p className="flex items-center justify-center gap-2 text-xs text-gray-300">
                                                <HiLockClosed className="size-3" aria-hidden="true" />
                                                <span>{t('secureCheckout')}</span>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                            <Button
                                className="sticky bottom-4 mt-6 md:hidden"
                                as="a"
                                href={checkoutLink}
                            >
                                {t('checkout')}
                            </Button>
                        </>
                    )}
                </div>
            )}
            {!!pageContent?.products?.length && (
                <div className="mt-10">
                    <h2 className="text-lg font-bold mb-2">{t('upselling.title')}</h2>
                    <Suspense fallback={<UpsellingProductsSkeletonLoader />}>
                        <UpsellingProducts products={pageContent.products} />
                    </Suspense>
                </div>
            )}
        </div>
    )
}
